import apiClient from './ApiClient';

export class OrderRepository {
	constructor(client) {
		this.client = client;
		this.basePath = '/api/orders';
	}

	/**
	 * Get all orders with filtering - Admin only
	 * @param {Object} params - Optional query parameters
	 * @param {string} params.status - Filter by status (processing, ready, completed, cancelled)
	 * @param {string} params.startDate - Format YYYY-MM-DDTHH:mm:ss
	 * @param {string} params.endDate - Format YYYY-MM-DDTHH:mm:ss
	 * @param {string} params.clientPhone - Filter by phone
	 * @returns {Promise<Array>} Array of orders
	 */
	async list(params = {}) {
		return this.client.get(this.basePath, params);
	}

	/**
	 * Get order by reference code - Public endpoint
	 * @param {string} referenceCode - Order reference code
	 * @returns {Promise<Object>} Order details
	 */
	async getByReferenceCode(referenceCode) {
		return this.client.get(`${this.basePath}/status/${referenceCode}`);
	}

	/**
	 * Create a new order - Admin only
	 * @param {Object} payload
	 * @param {string} payload.clientName - Client name
	 * @param {string} payload.clientPhone - Client phone
	 * @param {Array<Object>} payload.items - Array of order items
	 * @param {string} payload.items[].serviceItemId - Product UUID
	 * @param {number} payload.items[].quantity - Quantity
	 * @returns {Promise<{referenceCode: string}>}
	 */
	async create(payload) {
		return this.client.post(this.basePath, payload);
	}

	/**
	 * Update order status - Admin only
	 * @param {string} id - Order UUID
	 * @param {string} status - New status (processing, ready, completed, cancelled)
	 * @returns {Promise<{success: boolean}>}
	 */
	async updateStatus(id, status) {
		return this.client.patch(`${this.basePath}/${id}/status`, { status });
	}

	/**
	 * Bulk update order statuses - Admin only
	 * @param {string[]} orderIds - Array of Order UUIDs
	 * @param {string} status - New status for all orders (processing, ready, readyforpickup, completed, cancelled)
	 * @returns {Promise<{succeeded: number, failed: number, failedIds: string[]}>}
	 */
	async bulkUpdateStatus(orderIds, status) {
		return this.client.patch(`${this.basePath}/bulk-status`, { orderIds, status });
	}

	/**
	 * Get order by ID - Admin only
	 * @param {string} id - Order UUID
	 * @returns {Promise<Object>} Order details
	 */
	async getById(id) {
		return this.client.get(`${this.basePath}/${id}`);
	}

	/**
	 * Initiate Paystack payment for an order
	 * @param {string} id - Order UUID
	 * @param {string} clientEmail - Client email for Paystack
	 * @param {string} callbackUrl - URL Paystack redirects to after payment
	 * @returns {Promise<{authorizationUrl: string, reference: string}>}
	 */
	async initiatePayment(id, clientEmail, callbackUrl) {
		return this.client.post(`${this.basePath}/${id}/initiate-payment`, { clientEmail, callbackUrl });
	}

	/**
	 * Verify payment after Paystack redirect (card / Apple Pay only)
	 * @param {string} reference - Paystack payment reference from query param
	 * @returns {Promise<{success: boolean}>}
	 */
	async verifyPayment(reference) {
		return this.client.post(`${this.basePath}/verify-payment?reference=${encodeURIComponent(reference)}`);
	}

	/**
	 * Get paginated orders - Admin only
	 * @param {Object} params
	 * @param {number} params.page
	 * @param {number} params.pageSize
	 * @param {string} [params.status]
	 * @param {string} [params.startDate] - YYYY-MM-DD
	 * @param {string} [params.endDate]   - YYYY-MM-DD
	 * @param {string} [params.clientPhone]
	 * @returns {Promise<{items: Array, totalCount: number, totalPages: number, page: number, pageSize: number}>}
	 */
	async listPaged(params = {}) {
		return this.client.get(`${this.basePath}/paged`, params);
	}

	/**
	 * Calculate fee breakdown for a given subtotal
	 * @param {number} amount - Cart subtotal
	 * @param {string} companyPath - Company path slug
	 * @returns {Promise<{subtotal, tax, processingFee, total}>}
	 */
	async calculateFees(amount, companyPath) {
		return this.client.get('/api/fees/calculate', { amount, companyPath });
	}

	// Legacy methods for compatibility
	async update(id, payload) {
		// If payload contains status, use updateStatus instead
		if (payload.status) {
			return this.updateStatus(id, payload.status);
		}
		throw new Error('Direct order updates not supported. Use updateStatus for status changes.');
	}

	async remove(id) {
		// The API doesn't support deleting orders, only status changes
		return this.updateStatus(id, 'cancelled');
	}
}

export default new OrderRepository(apiClient);
